import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Toast } from '#components/toast/toast.js';
import { useTRPC } from '#src/trpc.js';
import { useAddToast } from '#state/stores/toast-store.js';

import type { HilProjectPreview } from '@cadstrom/backend/shared';

export interface DemoProjectsCloneDialogProps {
	open: boolean;
	onClose: () => void;
	/** The organization (by name) the demos are cloned into. */
	orgName: string;
	/** The org's current projects, used to detect already-added demos. */
	projects: HilProjectPreview[];
}

const STATE_LABELS: Record<string, string> = {
	pending: 'Waiting…',
	cloning: 'Cloning…',
	cloned: '✓ Cloned',
	failed: '✗ Failed',
};

/**
 * Staff-only dialog for cloning published demo projects into an existing org.
 * The view is driven by server-truth seed status: while a batch runs (started
 * here or elsewhere) it shows live progress and cannot start another; otherwise
 * it shows the catalog picker with already-added and currently-cloning demos
 * disabled.
 */
export function DemoProjectsCloneDialog({ open, onClose, orgName, projects }: DemoProjectsCloneDialogProps) {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const addToast = useAddToast();
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [startedThisSession, setStartedThisSession] = useState(false);
	const invalidatedForRef = useRef<string | undefined>(undefined);

	const { data: demoData } = useQuery({
		...trpc.organization.listDemoProjects.queryOptions({}),
		enabled: open,
	});
	const demoProjects = demoData?.projects ?? [];

	const { data: seedStatus } = useQuery({
		...trpc.organization.getDemoSeedStatusForOrg.queryOptions({ organizationName: orgName }),
		enabled: open,
		// Poll while a batch runs; a settled poll stops the interval.
		refetchInterval: (query) => (query.state.data?.status === 'running' ? 1500 : false),
	});

	const isRunning = seedStatus?.status === 'running';
	const isTerminal = seedStatus?.status === 'completed' || seedStatus?.status === 'failed';

	const addedSourceIds = useMemo(
		() => new Set(projects.map((project) => project.demoSourceProjectId).filter((id): id is string => id !== null)),
		[projects],
	);

	// Refresh the projects list once when a batch finishes so newly-cloned demos
	// appear and their "already added" state updates without a manual reload.
	useEffect(() => {
		if (!isTerminal || seedStatus === undefined) {
			return;
		}
		const key = `${seedStatus.status}:${seedStatus.completed}/${seedStatus.total}`;
		if (invalidatedForRef.current === key) {
			return;
		}
		invalidatedForRef.current = key;
		void queryClient.invalidateQueries({ queryKey: trpc.project.list.queryKey() });
	}, [isTerminal, seedStatus, queryClient, trpc.project.list]);

	const { mutateAsync: seedDemoProjects, isPending } = useMutation(
		trpc.organization.seedDemoProjects.mutationOptions(),
	);

	const showProgress = isRunning || (startedThisSession && isTerminal);

	const handleClose = () => {
		setSelectedIds(new Set());
		setStartedThisSession(false);
		onClose();
	};

	const toggle = (projectId: string) => {
		setSelectedIds((previous) => {
			const next = new Set(previous);
			if (next.has(projectId)) {
				next.delete(projectId);
			} else {
				next.add(projectId);
			}
			return next;
		});
	};

	const handleAdd = async () => {
		try {
			const result = await seedDemoProjects({ organizationName: orgName, demoProjectIds: Array.from(selectedIds) });
			if (result.workflowId === null) {
				setSelectedIds(new Set());
				addToast(
					<Toast severity='info' title='Nothing to add'>
						The selected demo projects are already in this organization.
					</Toast>,
				);
				return;
			}
			if (result.reason === 'alreadyRunning') {
				// A batch is already in flight for this org, so this selection was not
				// started. Keep it selected so it can be retried once that batch ends,
				// and surface the running batch's progress without claiming it as ours.
				addToast(
					<Toast severity='warning' title='Cloning already in progress'>
						A clone batch is already running for this organization. Wait for it to finish, then add these demos.
					</Toast>,
				);
				await queryClient.invalidateQueries({
					queryKey: trpc.organization.getDemoSeedStatusForOrg.queryKey({ organizationName: orgName }),
				});
				return;
			}
			setSelectedIds(new Set());
			setStartedThisSession(true);
			invalidatedForRef.current = undefined;
			// Surface the running batch immediately rather than waiting for the poll.
			await queryClient.invalidateQueries({
				queryKey: trpc.organization.getDemoSeedStatusForOrg.queryKey({ organizationName: orgName }),
			});
		} catch (error) {
			addToast(
				<Toast severity='error' title='Failed to start cloning'>
					{error instanceof Error ? error.message : 'An unexpected error occurred.'}
				</Toast>,
			);
		}
	};

	const progressValue =
		seedStatus !== undefined && seedStatus.total > 0 ? (seedStatus.completed / seedStatus.total) * 100 : 0;

	return (
		<Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
			{showProgress ?
				<>
					<DialogTitle>Cloning demo projects</DialogTitle>
					<DialogContent className='flex flex-col gap-4 pt-2'>
						<LinearProgress
							variant={seedStatus === undefined ? 'indeterminate' : 'determinate'}
							value={progressValue}
						/>
						<div className='flex flex-col gap-1'>
							{(seedStatus.perProject ?? []).map((project) => (
								<div key={project.sourceProjectId} className='flex items-center justify-between gap-2'>
									<span className='text-sm'>{project.name}</span>
									<span
										className={clsx(
											'text-xs',
											project.state === 'failed' ? 'text-red-11'
											: project.state === 'cloned' ? 'text-green-11'
											: 'text-gray-a10',
										)}
										title={project.reason}
									>
										{STATE_LABELS[project.state] ?? project.state}
									</span>
								</div>
							))}
						</div>
						{startedThisSession && isTerminal && seedStatus !== undefined && (
							<Alert
								severity={
									seedStatus.status !== 'completed' ? 'error'
									: seedStatus.perProject.some((project) => project.state === 'failed') ?
										'warning'
									:	'success'
								}
							>
								{summarize(seedStatus)}
							</Alert>
						)}
					</DialogContent>
					<DialogActions>
						<Button onClick={handleClose} variant='contained' disabled={isRunning}>
							<span>{isRunning ? 'Cloning…' : 'Done'}</span>
						</Button>
					</DialogActions>
				</>
			:	<>
					<DialogTitle>Add demo projects</DialogTitle>
					<DialogContent className='flex flex-col gap-2 pt-2'>
						{demoProjects.length === 0 ?
							<span className='text-sm text-gray-a10'>No demo projects are published.</span>
						:	demoProjects.map((project) => {
								const alreadyAdded = addedSourceIds.has(project.id);
								const disabled = alreadyAdded || isPending;
								return (
									<FormControlLabel
										key={project.id}
										disabled={disabled}
										control={
											<Checkbox
												checked={selectedIds.has(project.id)}
												onChange={() => toggle(project.id)}
												disabled={disabled}
											/>
										}
										label={
											<div className='flex flex-col'>
												<div className='flex items-center gap-2'>
													<span className='text-sm'>{project.name}</span>
													{alreadyAdded && <span className='text-xs text-gray-a10'>Already added</span>}
												</div>
												{project.description.length > 0 && (
													<span className='text-xs text-gray-a10'>{project.description}</span>
												)}
											</div>
										}
									/>
								);
							})
						}
					</DialogContent>
					<DialogActions>
						<Button onClick={handleClose} disabled={isPending}>
							<span>Cancel</span>
						</Button>
						<Button
							variant='contained'
							onClick={() => void handleAdd()}
							disabled={isPending || selectedIds.size === 0}
							loading={isPending}
						>
							<span>Add</span>
						</Button>
					</DialogActions>
				</>
			}
		</Dialog>
	);
}

/**
 * Builds the human-readable summary shown when a clone batch finishes.
 *
 * @param status - The terminal seed status.
 * @returns A sentence describing how many demos cloned and which failed.
 */
function summarize(status: {
	status: string;
	total: number;
	perProject: Array<{ state: string; name: string }>;
}): string {
	if (status.status !== 'completed') {
		return "Couldn't clone the demo projects: the run didn't complete.";
	}
	const cloned = status.perProject.filter((project) => project.state === 'cloned').length;
	const failedNames = status.perProject.filter((project) => project.state === 'failed').map((project) => project.name);
	if (failedNames.length === 0) {
		return `${cloned} of ${status.total} demo project(s) cloned.`;
	}
	return `${cloned} of ${status.total} cloned. Couldn't clone: ${failedNames.join(', ')}.`;
}
